import { type FC, useState } from 'react';
import type { SimulationScenario, ScenarioType, IntensityLevel, TimeHorizon } from '@/types/simulation';

// ---------------------------------------------------------------------------
// 195-country list for autocomplete (major + complete list via Claude knowledge)
// ---------------------------------------------------------------------------

const COUNTRIES_195 = [
  'Afghanistan','Albania','Algeria','Andorra','Angola','Antigua and Barbuda',
  'Argentina','Armenia','Australia','Austria','Azerbaijan',
  'Bahamas','Bahrain','Bangladesh','Barbados','Belarus','Belgium','Belize',
  'Benin','Bhutan','Bolivia','Bosnia and Herzegovina','Botswana','Brazil','Brunei',
  'Bulgaria','Burkina Faso','Burundi',
  'Cabo Verde','Cambodia','Cameroon','Canada','Central African Republic','Chad','Chile',
  'China','Colombia','Comoros','Congo','Costa Rica','Croatia','Cuba','Cyprus',
  'Czech Republic',
  'Denmark','Djibouti','Dominica','Dominican Republic',
  'DR Congo','Ecuador','Egypt','El Salvador','Equatorial Guinea','Eritrea','Estonia',
  'Eswatini','Ethiopia',
  'Fiji','Finland','France',
  'Gabon','Gambia','Georgia','Germany','Ghana','Greece','Grenada','Guatemala','Guinea',
  'Guinea-Bissau','Guyana',
  'Haiti','Honduras','Hungary',
  'Iceland','India','Indonesia','Iran','Iraq','Ireland','Israel','Italy',
  'Jamaica','Japan','Jordan',
  'Kazakhstan','Kenya','Kiribati','Kuwait','Kyrgyzstan',
  'Laos','Latvia','Lebanon','Lesotho','Liberia','Libya','Liechtenstein','Lithuania',
  'Luxembourg',
  'Madagascar','Malawi','Malaysia','Maldives','Mali','Malta','Marshall Islands',
  'Mauritania','Mauritius','Mexico','Micronesia','Moldova','Monaco','Mongolia',
  'Montenegro','Morocco','Mozambique','Myanmar',
  'Namibia','Nauru','Nepal','Netherlands','New Zealand','Nicaragua','Niger',
  'Nigeria','North Korea','North Macedonia','Norway',
  'Oman',
  'Pakistan','Palau','Panama','Papua New Guinea','Paraguay','Peru','Philippines',
  'Poland','Portugal',
  'Qatar',
  'Romania','Russia','Rwanda',
  'Saint Kitts and Nevis','Saint Lucia','Saint Vincent and the Grenadines','Samoa',
  'San Marino','São Tomé and Príncipe','Saudi Arabia','Senegal','Serbia','Seychelles',
  'Sierra Leone','Singapore','Slovakia','Slovenia','Solomon Islands','Somalia',
  'South Africa','South Korea','South Sudan','Spain','Sri Lanka','Sudan','Suriname',
  'Sweden','Switzerland','Syria',
  'Taiwan','Tajikistan','Tanzania','Thailand','Timor-Leste','Togo','Tonga',
  'Trinidad and Tobago','Tunisia','Turkey','Turkmenistan','Tuvalu',
  'Uganda','Ukraine','United Arab Emirates','United Kingdom','United States',
  'Uruguay','Uzbekistan',
  'Vanuatu','Vatican City','Venezuela','Vietnam',
  'Yemen',
  'Zambia','Zimbabwe',
];

// ---------------------------------------------------------------------------
// Scenario type definitions with icons and labels
// ---------------------------------------------------------------------------

interface ScenarioTypeOption {
  type: ScenarioType;
  icon: string;
  label: string;
  category: string;
  color: string;
}

const SCENARIO_OPTIONS: ScenarioTypeOption[] = [
  // Military
  { type: 'direct-attack', icon: '⚔️', label: 'Direct Attack', category: 'Military', color: 'border-red-700 hover:bg-red-950/40' },
  { type: 'nuclear-threat', icon: '☢️', label: 'Nuclear Threat', category: 'Military', color: 'border-red-500 hover:bg-red-950/60' },
  { type: 'naval-blockade', icon: '⚓', label: 'Naval Blockade', category: 'Military', color: 'border-red-700 hover:bg-red-950/40' },
  { type: 'proxy-war', icon: '🎯', label: 'Proxy War', category: 'Military', color: 'border-orange-700 hover:bg-orange-950/40' },
  { type: 'hybrid-warfare', icon: '🕵️', label: 'Hybrid Warfare', category: 'Military', color: 'border-orange-600 hover:bg-orange-950/40' },
  // Economic
  { type: 'trade-war', icon: '📊', label: 'Trade War / Tariffs', category: 'Economic', color: 'border-yellow-700 hover:bg-yellow-950/40' },
  { type: 'sanctions', icon: '🔒', label: 'Economic Sanctions', category: 'Economic', color: 'border-yellow-600 hover:bg-yellow-950/40' },
  { type: 'energy-cutoff', icon: '⚡', label: 'Energy Cutoff', category: 'Economic', color: 'border-yellow-700 hover:bg-yellow-950/40' },
  { type: 'tech-ban', icon: '💻', label: 'Tech Export Ban', category: 'Economic', color: 'border-yellow-600 hover:bg-yellow-950/40' },
  { type: 'currency-war', icon: '💱', label: 'Currency War', category: 'Economic', color: 'border-yellow-500 hover:bg-yellow-950/40' },
  // Political
  { type: 'coup', icon: '🎖️', label: 'Coup / Regime Change', category: 'Political', color: 'border-purple-700 hover:bg-purple-950/40' },
  { type: 'election-interference', icon: '🗳️', label: 'Election Interference', category: 'Political', color: 'border-purple-600 hover:bg-purple-950/40' },
  { type: 'territorial-annexation', icon: '🗺️', label: 'Territorial Annexation', category: 'Political', color: 'border-purple-700 hover:bg-purple-950/40' },
  { type: 'political-assassination', icon: '🎯', label: 'Political Assassination', category: 'Political', color: 'border-red-600 hover:bg-red-950/40' },
  // Diplomatic
  { type: 'diplomatic-break', icon: '🚫', label: 'Diplomatic Break / War Dec.', category: 'Diplomatic', color: 'border-sky-700 hover:bg-sky-950/40' },
  { type: 'alliance-formation', icon: '🤝', label: 'Alliance Formation', category: 'Diplomatic', color: 'border-sky-600 hover:bg-sky-950/40' },
  { type: 'un-crisis', icon: '🌐', label: 'UN Security Council Crisis', category: 'Diplomatic', color: 'border-sky-700 hover:bg-sky-950/40' },
  // Cyber
  { type: 'cyber-attack', icon: '💻', label: 'Cyber Attack', category: 'Cyber', color: 'border-cyan-700 hover:bg-cyan-950/40' },
  { type: 'space-weapons', icon: '🛸', label: 'Space Weaponization', category: 'Cyber', color: 'border-cyan-600 hover:bg-cyan-950/40' },
  { type: 'ai-arms-race', icon: '🤖', label: 'AI Arms Race', category: 'Cyber', color: 'border-cyan-500 hover:bg-cyan-950/40' },
  // Humanitarian
  { type: 'resource-war', icon: '⛽', label: 'Resource War', category: 'Humanitarian', color: 'border-emerald-700 hover:bg-emerald-950/40' },
  { type: 'climate-conflict', icon: '🌊', label: 'Climate Conflict', category: 'Humanitarian', color: 'border-emerald-600 hover:bg-emerald-950/40' },
];

// ---------------------------------------------------------------------------
// Country autocomplete input
// ---------------------------------------------------------------------------

const CountryInput: FC<{
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}> = ({ value, onChange, placeholder }) => {
  const [open, setOpen] = useState(false);
  const filtered = value.length > 0
    ? COUNTRIES_195.filter((c) => c.toLowerCase().includes(value.toLowerCase())).slice(0, 8)
    : [];

  return (
    <div className="relative">
      <input
        className="w-full bg-gray-900/80 border border-slate-700 rounded px-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-amber-500/60"
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        autoComplete="off"
      />
      {open && filtered.length > 0 && (
        <ul className="absolute z-10 mt-1 w-full bg-gray-900 border border-slate-700 rounded shadow-xl max-h-48 overflow-y-auto">
          {filtered.map((c) => (
            <li
              key={c}
              onMouseDown={() => { onChange(c); setOpen(false); }}
              className="px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800 cursor-pointer"
            >
              {c}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// ScenarioBuilder
// ---------------------------------------------------------------------------

interface ScenarioBuilderProps {
  onLaunch: (scenario: SimulationScenario) => void;
  onClose: () => void;
}

export const ScenarioBuilder: FC<ScenarioBuilderProps> = ({ onLaunch, onClose }) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedType, setSelectedType] = useState<ScenarioType | null>(null);
  const [initiator, setInitiator] = useState('');
  const [target, setTarget] = useState('');
  const [intensity, setIntensity] = useState<IntensityLevel>(3);
  const [timeHorizon, setTimeHorizon] = useState<TimeHorizon>('months');
  const [context, setContext] = useState('');

  const canProceed = selectedType && initiator.trim() && target.trim();
  const canLaunch = canProceed;

  const categories = [...new Set(SCENARIO_OPTIONS.map((o) => o.category))];

  const handleLaunch = () => {
    if (!selectedType || !initiator || !target) return;
    onLaunch({
      type: selectedType,
      initiator: initiator.trim(),
      target: target.trim(),
      intensity,
      timeHorizon,
      additionalContext: context.trim() || undefined,
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
        <div>
          <h2 className="text-base font-bold text-slate-100 tracking-wide">
            🎯 GEOPOLITICAL SIMULATION
          </h2>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Configure your scenario — Claude AI will simulate realistic outcomes
          </p>
        </div>
        <button onClick={onClose} className="text-slate-500 hover:text-slate-300 text-xl">✕</button>
      </div>

      {/* Step indicator */}
      <div className="flex gap-2 px-6 py-3 border-b border-slate-800/50">
        {([1, 2] as const).map((s) => (
          <div key={s} className={`flex items-center gap-1.5 text-xs font-semibold ${step === s ? 'text-amber-400' : 'text-slate-600'}`}>
            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${step === s ? 'bg-amber-500 text-black' : step > s ? 'bg-emerald-800 text-emerald-300' : 'bg-slate-800 text-slate-500'}`}>
              {step > s ? '✓' : s}
            </span>
            {s === 1 ? 'Scenario & Countries' : 'Parameters'}
          </div>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
        {step === 1 && (
          <>
            {/* Scenario type picker */}
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                Scenario Type
              </p>
              <div className="space-y-3">
                {categories.map((cat) => (
                  <div key={cat}>
                    <p className="text-[10px] text-slate-600 uppercase tracking-widest mb-1.5">{cat}</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                      {SCENARIO_OPTIONS.filter((o) => o.category === cat).map((opt) => (
                        <button
                          key={opt.type}
                          onClick={() => setSelectedType(opt.type)}
                          className={`flex items-center gap-2 px-3 py-2 rounded border text-left text-xs transition-all
                            ${selectedType === opt.type
                              ? 'border-amber-500 bg-amber-950/40 text-amber-200'
                              : `border-slate-700/60 text-slate-400 ${opt.color}`
                            }`}
                        >
                          <span className="text-base leading-none">{opt.icon}</span>
                          <span className="leading-tight">{opt.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Countries */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                  Initiator / Aggressor
                </p>
                <CountryInput value={initiator} onChange={setInitiator} placeholder="e.g. North Korea" />
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                  Target / Respondent
                </p>
                <CountryInput value={target} onChange={setTarget} placeholder="e.g. United States" />
              </div>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            {/* Selected summary */}
            <div className="bg-slate-900/60 border border-slate-700/50 rounded-lg p-3 text-xs">
              <div className="flex items-center gap-2 text-amber-400 font-semibold">
                <span>{SCENARIO_OPTIONS.find((o) => o.type === selectedType)?.icon}</span>
                <span>{SCENARIO_OPTIONS.find((o) => o.type === selectedType)?.label}</span>
              </div>
              <div className="text-slate-400 mt-1">
                <span className="text-slate-200">{initiator}</span>
                <span className="mx-2 text-slate-600">→</span>
                <span className="text-slate-200">{target}</span>
              </div>
            </div>

            {/* Intensity */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Intensity</p>
                <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                  intensity <= 2 ? 'bg-emerald-950 text-emerald-400' :
                  intensity === 3 ? 'bg-yellow-950 text-yellow-400' :
                  intensity === 4 ? 'bg-orange-950 text-orange-400' :
                  'bg-red-950 text-red-400'
                }`}>
                  {['', 'Low', 'Moderate', 'Significant', 'Severe', 'Extreme'][intensity]}
                </span>
              </div>
              <input
                type="range" min={1} max={5} value={intensity}
                onChange={(e) => setIntensity(parseInt(e.target.value) as IntensityLevel)}
                className="w-full accent-amber-500"
              />
              <div className="flex justify-between text-[10px] text-slate-600 mt-1">
                <span>Minor Incident</span>
                <span>Total War / Crisis</span>
              </div>
            </div>

            {/* Time horizon */}
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Time Horizon</p>
              <div className="grid grid-cols-4 gap-2">
                {(['days', 'weeks', 'months', 'years'] as TimeHorizon[]).map((h) => (
                  <button
                    key={h}
                    onClick={() => setTimeHorizon(h)}
                    className={`py-2 rounded border text-xs font-semibold capitalize transition-all
                      ${timeHorizon === h
                        ? 'border-amber-500 bg-amber-950/40 text-amber-300'
                        : 'border-slate-700/60 text-slate-500 hover:border-slate-600 hover:text-slate-400'
                      }`}
                  >
                    {h}
                  </button>
                ))}
              </div>
            </div>

            {/* Additional context */}
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                Additional Context <span className="text-slate-600 font-normal">(optional)</span>
              </p>
              <textarea
                value={context}
                onChange={(e) => setContext(e.target.value)}
                placeholder="e.g. 'US election just happened', 'China's economy is in recession', 'NATO is divided'..."
                rows={3}
                className="w-full bg-gray-900/80 border border-slate-700 rounded px-3 py-2 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-amber-500/60 resize-none"
              />
            </div>
          </>
        )}
      </div>

      {/* Footer nav */}
      <div className="flex items-center justify-between px-6 py-4 border-t border-slate-800">
        <button
          onClick={() => step === 1 ? onClose() : setStep(1)}
          className="px-4 py-2 text-xs font-semibold text-slate-400 hover:text-slate-200 border border-slate-700 rounded transition-colors"
        >
          {step === 1 ? 'Cancel' : '← Back'}
        </button>
        {step === 1 ? (
          <button
            onClick={() => setStep(2)}
            disabled={!canProceed}
            className="px-5 py-2 text-xs font-bold rounded bg-amber-600 hover:bg-amber-500 text-black disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Next: Parameters →
          </button>
        ) : (
          <button
            onClick={handleLaunch}
            disabled={!canLaunch}
            className="px-6 py-2 text-xs font-bold rounded bg-red-700 hover:bg-red-600 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors animate-pulse"
          >
            🚀 LAUNCH SIMULATION
          </button>
        )}
      </div>
    </div>
  );
};
